/**
 * Contexte élève : utilisateur Firebase + profil (année scolaire) + stats + progression,
 * en temps réel. Un seul jeu d'abonnements RTDB pour toute l'app.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { ref, onValue, off } from "firebase/database";
import { auth, db } from "../firebase";
import {
  DEFAULT_STATS,
  ProgressMap,
  StudentProfile,
  StudentStats,
  isParentProfile,
  subscribeProfile,
  subscribeProgress,
  subscribeStats,
} from "../services/progressService";

/** Fournisseurs gérés côté serveur (Discord) : users/$uid/providers/<id> */
export type ServerProviders = Record<string, { id?: string; username?: string; linkedAt?: number }>;

interface StudentContextValue {
  user: User | null;
  /** true tant que l'état d'auth initial n'est pas connu */
  authLoading: boolean;
  profile: StudentProfile | null;
  /** true tant que le profil n'a pas été lu au moins une fois */
  profileLoading: boolean;
  /** Compte parent (profile.role === "parent") : onglets et écrans dédiés, pas de parcours élève. */
  isParent: boolean;
  stats: StudentStats;
  progress: ProgressMap;
  providers: ServerProviders;
}

const StudentContext = createContext<StudentContextValue>({
  user: null,
  authLoading: true,
  profile: null,
  profileLoading: true,
  isParent: false,
  stats: DEFAULT_STATS,
  progress: {},
  providers: {},
});

export const useStudent = () => useContext(StudentContext);

export const StudentProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [stats, setStats] = useState<StudentStats>(DEFAULT_STATS);
  const [progress, setProgress] = useState<ProgressMap>({});
  const [providers, setProviders] = useState<ServerProviders>({});

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
      if (!u) {
        setProfile(null);
        setProfileLoading(false);
        setStats(DEFAULT_STATS);
        setProgress({});
        setProviders({});
      } else {
        setProfileLoading(true);
      }
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    const uid = user.uid;
    const provRef = ref(db, `users/${uid}/providers`);
    const provHandler = onValue(provRef, (snap) => setProviders((snap.val() as ServerProviders) || {}));
    const unsubs = [
      subscribeProfile(uid, (p) => {
        setProfile(p);
        setProfileLoading(false);
      }),
      subscribeStats(uid, setStats),
      subscribeProgress(uid, setProgress),
      () => off(provRef, "value", provHandler),
    ];
    return () => unsubs.forEach((u) => u());
  }, [user]);

  const isParent = isParentProfile(profile);

  return (
    <StudentContext.Provider value={{ user, authLoading, profile, profileLoading, isParent, stats, progress, providers }}>
      {children}
    </StudentContext.Provider>
  );
};
